const DEFAULT_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://femtyz-admin.pixoatic.in/";

const inflightGetRequests = new Map();

const AUTH_FLOW_PATHS = new Set([
  "api/login",
  "api/signup",
  "api/verify_otp",
  "api/resend_otp",
  "api/forgot_password",
  "api/reset_password",
]);

let authExpiredNotified = false;

export function isAuthErrorMessage(text) {
  return /invalid or expired token|unauthorized|jwt expired|token expired/i.test(String(text || ""));
}

export function isAuthErrorResponse({ status, data } = {}) {
  if (status === 401 || status === 403) return true;
  const errors = Array.isArray(data?.errors) ? data.errors : [];
  const message = String(data?.message || "");
  const combined = [...errors.map((e) => String(e)), message].join(" ");
  return isAuthErrorMessage(combined);
}

function isAuthFlowPath(path) {
  const normalized = String(path || "").replace(/^\//, "").split("?")[0].toLowerCase();
  return AUTH_FLOW_PATHS.has(normalized);
}

function notifyAuthExpired(data) {
  if (authExpiredNotified) return;
  authExpiredNotified = true;
  window.setTimeout(() => {
    authExpiredNotified = false;
  }, 4000);

  const errors = Array.isArray(data?.errors) ? data.errors : [];
  const message =
    errors.filter(Boolean).join("\n") ||
    String(data?.message || "").trim() ||
    "Invalid or expired token";

  window.dispatchEvent(
    new CustomEvent("femtyz:auth-expired", {
      detail: { message },
    })
  );
}

function getToken() {
  try {
    return localStorage.getItem("femtyz_token");
  } catch {
    return null;
  }
}

export async function apiRequest(
  path,
  { method = "GET", body, headers, token: tokenOverride, signal } = {}
) {
  const token = tokenOverride !== undefined ? tokenOverride : getToken();
  const normalizedMethod = String(method || "GET").toUpperCase();
  const url = `${DEFAULT_BASE_URL}${path}`;

  const isDedupableGet = normalizedMethod === "GET" && !body && !signal;
  const inflightKey = isDedupableGet
    ? `${normalizedMethod} ${url} :: ${token ? "auth" : "anon"}`
    : "";

  if (isDedupableGet && inflightGetRequests.has(inflightKey)) {
    return inflightGetRequests.get(inflightKey);
  }

  const requestPromise = (async () => {
    const res = await fetch(url, {
      method: normalizedMethod,
      signal,
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(headers || {}),
      },
      body: body ? JSON.stringify(body) : undefined,
    });

    const data = await res.json().catch(() => ({}));
    const result = { ok: res.ok, status: res.status, data };

    if (
      token &&
      !isAuthFlowPath(path) &&
      !result.ok &&
      isAuthErrorResponse(result)
    ) {
      notifyAuthExpired(data);
    }

    return result;
  })();

  if (isDedupableGet) {
    inflightGetRequests.set(inflightKey, requestPromise);
    requestPromise.finally(() => {
      inflightGetRequests.delete(inflightKey);
    });
  }

  return requestPromise;
}

export const API_BASE_URL = DEFAULT_BASE_URL;

export function getAuthHeader() {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

